import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { NavigationEnd, RouterModule } from '@angular/router';
import { Action } from '../../../models/core/action';
import { ViewService } from '../../../services/view/view.service';
import { BaseComponent } from '../../base/base.component';

let claimMenuItemId = 0;

@Component({
  selector: 'app-claim-menu-item',
  imports: [CommonModule, OverlayModule, RouterModule],
  template: `
    <button
      type="button"
      class="
        tw-px-[16px]
        tw-font-bold
        tw-h-[50px]
        tw-flex tw-items-center
        tw-bg-transparent
        tw-border-0
        tw-border-solid
        hover:tw-bg-[var(--sed-grey-60-color)]
        hover:tw-text-[var(--sed-dynamic-black-color)]
        tw-cursor-pointer
        tw-text-[var(--sed-black-60-color)]
        menu-row-item
        menu-item-focus
        "
      [style.border-bottom]="
        item.active
          ? '5px solid var(--sed-sedgwick-green-color)'
          : '5px solid transparent'
      "
      [style.border-top]="'5px solid transparent'"
      cdkOverlayOrigin
      #trigger="cdkOverlayOrigin"
      #triggerEl
      (mouseenter)="openSubmenu()"
      (mouseleave)="closeSubmenu()"
      [ngClass]="{
        'active-link': item.active,
        'inactive-link': !item.active,
      }"
      (click)="itemClick(item)"
      (keydown)="itemClick(item, $event)"
      role="menuitem"
      [attr.aria-expanded]="item.items?.length ? isOpen : null"
      [attr.aria-haspopup]="item.items?.length ? true : null"
      [attr.aria-current]="item.active ? 'true' : null"
    >
      <span>{{ item.label }}</span>
    </button>
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayOpen]="isOpen"
      [cdkConnectedOverlayPositions]="positions"
      [cdkConnectedOverlayFlexibleDimensions]="true"
      (detach)="isOpen = false"
    >
      <ul
        [id]="menuId"
        class="tw-flex tw-flex-col
        tw-bg-[var(--sed-warm-white-color)]
        tw-border
        tw-drop-shadow-md
        tw-min-w-48
        tw-max-h-[480px]
        tw-overflow-y-auto
        submenu-panel"
        (mouseenter)="openSubmenu()"
        (mouseleave)="closeSubmenu()"
        [class.tw-hidden]="item.items.length === 0"
        role="menu"
      >
        @for (subItem of item.items; track subItem) {
          @if (subItem.authorized) {
            <li role="none">
              <button
                type="button"
                class="
                  tw-w-full
                  tw-px-[16px]
                  tw-font-bold
                  tw-h-[40px]
                  tw-flex tw-items-center
                  tw-bg-transparent
                  tw-border-0
                  hover:tw-bg-[var(--sed-grey-60-color)]
                  hover:tw-text-[var(--sed-dynamic-black-color)]
                  tw-cursor-pointer
                  tw-text-[var(--sed-black-60-color)]
                  tw-text-left
                  sub-item-visible
                  menu-item-focus
                "
                [ngClass]="{
                  'active-sub-link': subItem.active,
                }"
                (click)="itemClick(subItem)"
                (keydown)="itemClick(subItem, $event)"
                role="menuitem"
                [attr.aria-current]="subItem.active ? 'true' : null"
              >
                {{ subItem.label }}
              </button>
            </li>
          }
        }
      </ul>
    </ng-template>
  `,
  styles: `
    /* Global mobile rules wrap every .tw-flex; column-wrapping would turn the
       capped-height submenu into multiple columns instead of scrolling. */
    .submenu-panel {
      flex-wrap: nowrap;
      overflow-x: hidden;
    }
    .active-link {
      color: var(--sed-dynamic-black-color);
    }
    .active-link:hover {
      background-color: var(--sed-warm-white-color);
    }
    .active-sub-link {
      color: var(--sed-dynamic-black-color);
      background-color: var(--sed-grey-100-color);
    }
  `,
})
export class ClaimMenuItemComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private viewService = inject(ViewService);
  private cdr = inject(ChangeDetectorRef);

  @Input()
  item: Action;

  @ViewChild('triggerEl')
  triggerEl: ElementRef<HTMLButtonElement>;

  readonly menuId = `claim-menu-${++claimMenuItemId}`;

  isOpen = false;

  rootUrl: string;

  opened: number;

  positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];

  constructor() {
    super();
    this.rootUrl = '/' + this.viewService.buildUrl([]).join('/');
    this.dispose.subs = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setActiveItem(event.urlAfterRedirects ?? event.url);
      }
    });
  }

  ngOnInit(): void {
    // Use router.url so active state is correct on load (location.pathname can be stale).
    this.setActiveItem(this.router.url);
    // Run again after a tick in case URL wasn't set yet when this component initialized.
    queueMicrotask(() => this.setActiveItem(this.router.url));
  }

  closeSubmenu() {
    const lastOpened = this.opened;
    this.dispose.timeouts = setTimeout(() => {
      if (lastOpened === this.opened) {
        this.isOpen = false;
      }
    }, 10);
  }

  openSubmenu() {
    this.opened = Date.now();
    this.isOpen = true;
  }

  private setActiveItem(url: string) {
    // Derive relative path from URL structure (view/claim/:source/:id/segment) so we
    // don't depend on currentClaim() being set yet when NavigationEnd fires (e.g. Plan Summary).
    const pathOnly = url.split('?')[0].split('#')[0];
    const segments = pathOnly.split('/').filter(Boolean);
    const viewIdx = segments.findIndex(
      (s, i) => s === 'view' && segments[i + 1] === 'claim',
    );
    const relativeUrl =
      viewIdx >= 0
        ? segments
            .slice(viewIdx + 4)
            .join('/')
            .replace(/\/$/, '')
        : '';
    // check top-level item
    this.item.active =
      this.item.routerLink != null && this.item.routerLink === relativeUrl;
    for (const subItem of this.item.items) {
      const isExactMatch = subItem.routerLink === relativeUrl;
      const isChildPath =
        subItem.routerLink && relativeUrl.startsWith(subItem.routerLink + '/');
      subItem.active = isExactMatch || isChildPath;
      if (subItem.active) {
        this.item.active = true;
      }
    }
    // Force this component's view to update so the green underline (active-link) shows.
    this.cdr.detectChanges();
  }

  private isActivateKey(event: KeyboardEvent): boolean {
    return event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
  }

  private getSubmenuItems(): HTMLElement[] {
    const panel = document.getElementById(this.menuId);
    if (!panel) {
      return [];
    }
    return Array.from(
      panel.querySelectorAll<HTMLElement>('[role="menuitem"]'),
    );
  }

  private focusTrigger() {
    this.triggerEl?.nativeElement?.focus();
  }

  private focusSubmenuItem(index: number) {
    const items = this.getSubmenuItems();
    if (!items.length) {
      return;
    }
    const target = items[Math.max(0, Math.min(index, items.length - 1))];
    target?.focus();
  }

  itemClick(item: Action, event: KeyboardEvent = null) {
    // toggle
    const wasOpen = this.isOpen;
    if (!event || this.isActivateKey(event)) {
      if (event && this.isActivateKey(event)) {
        event.preventDefault();
      }
      this.isOpen = !this.isOpen;
    }
    if (event?.key === 'ArrowDown') {
      event.preventDefault();
      this.isOpen = true;
    }
    if (event?.key === 'ArrowUp' && item?.items?.length) {
      event.preventDefault();
      this.isOpen = false;
    }
    const toggled = this.isOpen !== wasOpen;

    // click / activate navigation
    if (
      (!event || this.isActivateKey(event)) &&
      item?.routerLink !== undefined &&
      item?.routerLink !== null
    ) {
      const absoluteUrl = [this.rootUrl, item?.routerLink]
        .filter((x) => x)
        .join('/');
      this.router.navigateByUrl(absoluteUrl);
    }

    if (event?.key === 'Escape') {
      event.preventDefault();
      this.isOpen = false;
      this.focusTrigger();
    }

    if (event?.key === 'Tab') {
      const items = this.getSubmenuItems();
      if (items.length) {
        const index = items.indexOf(document.activeElement as HTMLElement);
        if (index >= 0) {
          event.preventDefault();
          if (event.shiftKey) {
            this.focusSubmenuItem(index === 0 ? items.length - 1 : index - 1);
          } else {
            this.focusSubmenuItem(index === items.length - 1 ? 0 : index + 1);
          }
        }
      }
    }

    // focus first submenu item when menu opens (delay for CDK overlay attach)
    if (toggled && this.isOpen && item?.items?.length) {
      this.cdr.detectChanges();
      setTimeout(() => this.focusSubmenuItem(0), 100);
    }

    if (event?.key === 'ArrowDown' && !item?.items?.length) {
      event.preventDefault();
      const items = this.getSubmenuItems();
      const index = items.indexOf(document.activeElement as HTMLElement);
      if (index >= 0 && index < items.length - 1) {
        this.focusSubmenuItem(index + 1);
      }
    }
    if (event?.key === 'ArrowUp' && !item?.items?.length) {
      event.preventDefault();
      const items = this.getSubmenuItems();
      const index = items.indexOf(document.activeElement as HTMLElement);
      if (index > 0) {
        this.focusSubmenuItem(index - 1);
      }
    }
    if (event?.key === 'ArrowRight') {
      event.preventDefault();
      const menuItems = Array.from(
        document.querySelectorAll<HTMLElement>('.menu-row-item'),
      );
      const index = menuItems.indexOf(document.activeElement as HTMLElement);
      if (index >= 0 && index < menuItems.length - 1) {
        menuItems[index + 1]?.focus();
      }
    }
    if (event?.key === 'ArrowLeft') {
      event.preventDefault();
      const menuItems = Array.from(
        document.querySelectorAll<HTMLElement>('.menu-row-item'),
      );
      const index = menuItems.indexOf(document.activeElement as HTMLElement);
      if (index > 0) {
        menuItems[index - 1]?.focus();
      }
    }
  }
}
